/**
 * CreatePollSheet — quick poll composer for Chalk Talk.
 *
 * One title + 2-5 options + optional close date. Stays deliberately
 * lightweight; richer (multi-choice, audience targeting) features can
 * surface in an "advanced" disclosure later.
 */
import { useState } from "react";
import { Plus, Vote, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { BottomSheet, ChalkActionButton } from "./primitives";

export default function CreatePollSheet({ isOpen, teamId, onDismiss, onCreated }) {
  const [title, setTitle] = useState("");
  const [pollType, setPollType] = useState("single");
  const [options, setOptions] = useState(["", ""]);
  const [busy, setBusy] = useState(false);

  // yes/no polls have a fixed option set — we hide the option editor
  // when the user picks that mode.
  const isYesNo = pollType === "yes_no";

  function setOpt(i, v) {
    const next = [...options];
    next[i] = v;
    setOptions(next);
  }
  function addOpt() {
    if (options.length >= 5) return;
    setOptions([...options, ""]);
  }
  function removeOpt(i) {
    if (options.length <= 2) return;
    setOptions(options.filter((_, idx) => idx !== i));
  }

  async function submit() {
    if (!title.trim()) {
      toast.error("Give your poll a question.");
      return;
    }
    let cleaned;
    if (isYesNo) {
      cleaned = ["Yes", "No"];
    } else {
      cleaned = options.map((o) => o.trim()).filter(Boolean);
      if (cleaned.length < 2) {
        toast.error("At least two options.");
        return;
      }
    }
    setBusy(true);
    try {
      const data = await api.createPoll(teamId, {
        title: title.trim(),
        options: cleaned,
        poll_type: pollType,
      });
      toast.success("Poll posted.");
      onCreated && onCreated(data.poll_id);
      setTitle("");
      setOptions(["", ""]);
      setPollType("single");
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't create poll.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title="Create a poll" onDismiss={onDismiss}>
      <div data-testid="create-poll-sheet" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", fontWeight: 700, marginBottom: 6 }}>
            Question
          </div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What time works best for practice?"
            maxLength={140}
            autoFocus
            data-testid="poll-title"
            style={{
              width: "100%", border: "0.5px solid var(--chalk-border)",
              borderRadius: 10, padding: "11px 12px", fontSize: 16,
              background: "#FBF8F2", color: "var(--chalk-board)", outline: "none",
            }}
          />
        </label>
        {/* Poll type chips. Matches the backend's three supported modes
            (single / multi / yes_no) so coaches don't have to guess what
            "Type=single" means. */}
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", fontWeight: 700, marginBottom: 6 }}>
            Vote type
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[
              { value: "single", label: "Pick one" },
              { value: "multi", label: "Pick many" },
              { value: "yes_no", label: "Yes / No" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPollType(opt.value)}
                data-testid={`poll-type-${opt.value}`}
                style={{
                  flex: 1, padding: "8px 10px", borderRadius: 8,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  background: pollType === opt.value ? "var(--chalk-board)" : "transparent",
                  color: pollType === opt.value ? "#F5F0E8" : "var(--chalk-board)",
                  border: "0.5px solid var(--chalk-border)",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {!isYesNo && (
          <div>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", fontWeight: 700, marginBottom: 6 }}>
              Options
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {options.map((opt, i) => (
                <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input
                    value={opt}
                    onChange={(e) => setOpt(i, e.target.value)}
                    placeholder={`Option ${i + 1}`}
                    data-testid={`poll-option-input-${i}`}
                    style={{
                      flex: 1, border: "0.5px solid var(--chalk-border)",
                      borderRadius: 10, padding: "10px 12px", fontSize: 15,
                      background: "#FBF8F2", color: "var(--chalk-board)", outline: "none",
                    }}
                  />
                  {options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOpt(i)}
                      aria-label="Remove option"
                      style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: "transparent", border: "0.5px solid var(--chalk-border)",
                        color: "var(--chalk-muted)", cursor: "pointer",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 5 && (
              <button
                type="button"
                onClick={addOpt}
                data-testid="poll-add-option"
                style={{
                  marginTop: 8, padding: "6px 12px", borderRadius: 8,
                  background: "transparent",
                  border: "0.5px dashed var(--chalk-border)",
                  color: "var(--green-mid)", fontWeight: 700, fontSize: 11,
                  letterSpacing: "0.06em", textTransform: "uppercase",
                  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
                }}
              >
                <Plus size={12} aria-hidden="true" /> Add option
              </button>
            )}
          </div>
        )}
        <ChalkActionButton
          testId="poll-submit"
          variant="primary"
          label={busy ? "Posting…" : "Post poll"}
          icon={<Vote size={16} aria-hidden="true" />}
          loading={busy}
          onClick={submit}
        />
      </div>
    </BottomSheet>
  );
}
